import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  PageHeader,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
  Spin
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { OrderAddress } from '../../../custom_types/address-page';
import { Country, COUNTRY_NAMES } from '../../../shared/utils/constants';
import {
  fetchsClientAccountsHandler,
  selectClientAccounts
} from '../../../redux/settings/carriersSlice';
import { selectLoading, selectRates } from '../../../redux/rate.slice';
import { UserShippingRateRequest } from '../../../custom_types/redux-types';
import { selectCurUser } from '../../../redux/user/userSlice';

const { Text } = Typography;

// 定义表格列配置
const columns = [
  {
    title: '物流渠道',
    dataIndex: 'channel',
    key: 'channel'
  },
  {
    title: '总重量(LB)',
    dataIndex: 'totalWeight',
    key: 'totalWeight'
  },
  {
    title: '总费用($)',
    dataIndex: 'totalCost',
    key: 'totalCost',
    render: (text: any) => {
      if (text === '查询失败') {
        return <Text type="danger">查询失败</Text>;
      }
      return text;
    }
  }
];

// 定义API响应类型
interface RateApiResponse {
  timestamp: string;
  channelId: string;
  totalAmt: string;
  currency: string;
}

// 定义API请求参数类型
interface RateApiRequest {
  token: string;
  channelId: string;
  shipTo: {
    name: string;
    phone: string;
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  packageList: Array<{
    weight: number;
    length: number;
    width: number;
    height: number;
    count: number;
  }>;
}

const PriceOrderPage = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const clientAccounts = useSelector(selectClientAccounts);
  const loading = useSelector(selectLoading);
  const rates = useSelector(selectRates);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const user = useSelector(selectCurUser);

  // 直接从当前用户信息中获取 token
  const clientToken = user.apiToken;

  useEffect(() => {
    dispatch(fetchsClientAccountsHandler());
  }, [dispatch]);

  // 调用新的API接口
  const checkRateApi = async (
    requestData: RateApiRequest
  ): Promise<RateApiResponse | null> => {
    try {
      const response = await fetch('https://api.eksshipping.com/api/rate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API调用失败:', error);
      return null;
    }
  };

  const searchRateHandler = async () => {
    try {
      const values = await form.validateFields();
      setIsSearching(true);
      setSearchResults([]); // 清空之前的查询结果

      const packages = values.packages || [];
      const totalWeight = packages
        .reduce((sum: number, p: any) => sum + (p.weight || 0), 0)
        .toFixed(2);

      // 使用 Promise.all 并行查询所有物流渠道
      const ratePromises = clientAccounts.map(async (account) => {
        // 准备API请求参数
        const requestData: RateApiRequest = {
          token: clientToken, // 使用提供的token
          channelId: account.accountId, // 使用账户ID作为channelId
          shipTo: {
            name: 'Test Name', // 添加姓名字段或使用默认值
            phone: '6137678888', // 添加电话字段或使用默认值
            email: 'test@gmail.com', // 添加邮箱字段或使用默认值
            address1: values.street1 || '',
            address2: values.street2 || '',
            city: values.city || '',
            state: values.state || '',
            country: values.country || 'US',
            zipCode: values.zip || ''
          },
          packageList: packages.map((p: any) => ({
            weight: p.weight || 0,
            length: p.length || 0,
            width: p.width || 0,
            height: p.height || 0,
            count: 1 // 默认为1个包裹
          }))
        };

        try {
          // 调用新API
          const response = await checkRateApi(requestData);

          if (response && response.totalAmt) {
            return {
              key: account.accountId,
              channel: account.accountId,
              totalWeight,
              totalCost: response.totalAmt,
              currency: response.currency,
              success: true
            };
          }
          return null;
        } catch (error) {
          console.error(`查询渠道 ${account.accountId} 失败:`, error);
          return null;
        }
      });

      // 等待所有查询完成
      const results = await Promise.all(ratePromises);

      // 过滤掉失败的查询结果
      const successfulResults = results.filter((result) => result !== null);

      // 更新页面结果
      setSearchResults(successfulResults);

      // 统一反馈查询结果
      if (successfulResults.length === 0) {
        message.warning('所有物流渠道查询均失败，请检查地址或包裹信息');
      } else {
        message.success(
          `成功查询到 ${successfulResults.length} 个物流渠道的价格`
        );
      }
    } catch (error) {
      console.error('表单验证或查询失败:', error);
      message.error('请检查表单填写是否正确');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div>
      <PageHeader title="费用查询" />
      <Row gutter={24}>
        <Col span={11}>
          <Form form={form} layout="vertical">
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 添加收件人信息字段 */}
              <Row gutter={10}>
                <Col span={12}>
                  <Form.Item
                    label="地址1"
                    name="street1"
                    rules={[{ required: true, message: '需要地址1' }]}
                  >
                    <Input placeholder="3900 Hamner Ave" />
                  </Form.Item>
                  <Form.Item
                    label="州/省"
                    name="state"
                    rules={[{ required: true, message: '需要州/省' }]}
                  >
                    <Input placeholder="CA" />
                  </Form.Item>
                  <Form.Item
                    label="邮编"
                    name="zip"
                    rules={[{ required: true, message: '需要邮编' }]}
                  >
                    <Input placeholder="91752" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="地址2" name="street2">
                    <Input placeholder="Unit 2" />
                  </Form.Item>
                  <Form.Item
                    label="城市"
                    name="city"
                    rules={[{ required: true, message: '需要城市' }]}
                  >
                    <Input placeholder="Eastvale" />
                  </Form.Item>
                  <Form.Item
                    label="国家"
                    name="country"
                    initialValue={Country.USA}
                    rules={[{ required: true, message: '需要国家' }]}
                  >
                    <Select>
                      <Select.Option value={Country.USA}>
                        {COUNTRY_NAMES[Country.USA]}
                      </Select.Option>
                      <Select.Option value={Country.CHINA}>
                        {COUNTRY_NAMES[Country.CHINA]}
                      </Select.Option>
                      <Select.Option value={Country.AUSTRALIA}>
                        {COUNTRY_NAMES[Country.AUSTRALIA]}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <div>
                <Form.List name="packages">
                  {(fields, { add, remove }) => {
                    return (
                      <div>
                        {fields.map((field, index) => (
                          <div key={`${field.name}_${field.key}`}>
                            <Space>
                              <Form.Item
                                label="重量(LB)"
                                name={[field.name, 'weight']}
                                initialValue={1}
                                rules={[
                                  { required: true, message: '需要重量' }
                                ]}
                              >
                                <InputNumber min={0.01} step={0.1} />
                              </Form.Item>
                              <Form.Item
                                label="长(IN)"
                                name={[field.name, 'length']}
                                initialValue={1}
                                rules={[
                                  { required: true, message: '需要长度' }
                                ]}
                              >
                                <InputNumber min={0.01} step={0.1} />
                              </Form.Item>
                              <Form.Item
                                label="宽(IN)"
                                name={[field.name, 'width']}
                                initialValue={1}
                                rules={[
                                  { required: true, message: '需要宽度' }
                                ]}
                              >
                                <InputNumber min={0.01} step={0.1} />
                              </Form.Item>
                              <Form.Item
                                label="高(IN)"
                                name={[field.name, 'height']}
                                initialValue={1}
                                rules={[
                                  { required: true, message: '需要高度' }
                                ]}
                              >
                                <InputNumber min={0.01} step={0.1} />
                              </Form.Item>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  style={{ fontSize: '14px', marginTop: '8px' }}
                                  className="dynamic-delete-button"
                                  onClick={() => {
                                    remove(field.name);
                                  }}
                                />
                              )}
                            </Space>
                          </div>
                        ))}

                        <Row style={{ marginBottom: '24px' }}>
                          <Col push={4} xl={16}>
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() => {
                                  add();
                                }}
                                style={{ width: '100%' }}
                              >
                                <PlusOutlined /> 添加包裹
                              </Button>
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    );
                  }}
                </Form.List>
              </div>
              <Button
                type="primary"
                onClick={searchRateHandler}
                loading={isSearching}
                disabled={clientAccounts.length === 0}
              >
                {isSearching ? '查询中...' : '查询价格'}
              </Button>
              {clientAccounts.length > 0 && (
                <Text type="secondary">
                  将并行查询 {clientAccounts.length} 个物流渠道
                </Text>
              )}
            </Space>
          </Form>
        </Col>
        <Col span={12}>
          <Card
            bordered={false}
            title="物流收费"
            extra={
              searchResults.length > 0 && (
                <Text type="secondary">
                  共 {searchResults.length} 个渠道查询成功
                </Text>
              )
            }
          >
            {isSearching ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>正在并行查询所有物流渠道...</Text>
                </div>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={searchResults}
                pagination={false}
                rowKey="key"
                locale={{
                  emptyText:
                    searchResults.length === 0
                      ? '请点击"查询价格"按钮获取物流费用信息'
                      : '所有渠道查询失败，请检查地址或包裹信息'
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PriceOrderPage;
